import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { LoginActions } from '../../Services/_actions/LoginAction/LoginAction.ts';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import LoadingButton from '../Shared/Loading/index.tsx';

const ModalForms = React.lazy(() => import('../Shared/Modals/index.js'));

interface ForgotPasswordModalProps {
    open: boolean;
    onClose: (data?: any, type?: any) => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
    open,
    onClose,
}) => {
    const action = LoginActions();
    const { showSnackBar } = useSnackBar();

    const [forgotPassword, setForgotPassword] = React.useState({
        type: 'username',
        value: '',
    });

    const [forgotPasswordError, setForgotPasswordError] =
        React.useState('');

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const primaryColor = '#6C5CE7'; // change to your brand color

    const handleSubmitForgotPassword = async (e: any) => {
        e.preventDefault();

        const inputValue = forgotPassword.value.trim();

        if (!inputValue) {
            setForgotPasswordError(
                `${
                    forgotPassword.type === 'email'
                        ? 'Email'
                        : 'Username'
                } is required.`
            );
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                username:
                    forgotPassword.type === 'username'
                        ? inputValue
                        : '',
                email:
                    forgotPassword.type === 'email'
                        ? inputValue
                        : '',
            };

            const response = await action.forgotPassword(payload);

            if (response?.status) {
                setForgotPasswordError('');

                showSnackBar(
                    'Password reset link sent successfully.',
                    'success'
                );

                setForgotPassword({
                    type: 'username',
                    value: '',
                });

                onClose(false);
            } else {
                const errorMessage =
                    response?.data?.general_error ||
                    response?.data?.message ||
                    'Failed to send password reset link.';

                setForgotPasswordError(errorMessage);
                showSnackBar(errorMessage, 'error');
            }
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.data?.general_error ||
                error?.response?.data?.general_error ||
                'Failed to send password reset link.';

            setForgotPasswordError(errorMessage);
            showSnackBar(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalForms
            show={open}
            handleClose={() => onClose(false)}
            modalHeader="Forgot Password"
            size="md"
            modalBody={
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ px: 1, py: 1 }}
                >
                    <TextField
                        id="forgot-password-input"
                        placeholder={
                            forgotPassword.type === 'email'
                                ? 'Enter your email'
                                : 'Enter your username'
                        }
                        type={
                            forgotPassword.type === 'email'
                                ? 'email'
                                : 'text'
                        }
                        size="small"
                        fullWidth
                        autoComplete={
                            forgotPassword.type === 'email'
                                ? 'email'
                                : 'username'
                        }
                        value={forgotPassword.value}
                        onChange={(e) => {
                            setForgotPassword({
                                ...forgotPassword,
                                value: e.target.value,
                            });
                            setForgotPasswordError('');
                        }}
                        error={Boolean(forgotPasswordError)}
                        helperText={
                            forgotPasswordError ||
                            `Enter your ${
                                forgotPassword.type === 'email'
                                    ? 'email address'
                                    : 'username'
                            }`
                        }
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                backgroundColor: '#FAFAFA',
                            },
                        }}
                    />
                </Box>
            }
            modalFooter={
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    mt={2}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onClose(false)}
                        sx={{
                            mr: 1,
                            borderRadius: '8px',
                            textTransform: 'none',
                        }}
                    >
                        Cancel
                    </Button>

                    <LoadingButton
                        variant="contained"
                        size="small"
                        buttonName="Submit"
                        isLoading={isSubmitting}
                        onClickButton={handleSubmitForgotPassword}
                    />
                </Box>
            }
        />
    );
};

export default ForgotPasswordModal;